import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  ChevronUpDownIcon,
  EyeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const StockMovementsTab = () => {
  const navigate = useNavigate();
  const { 
    products, 
    stockItems,
    warehouses,
    fetchAllData
  } = useAppData();
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };
    
    loadData();
  }, [fetchAllData]);

  // Get product name from ID
  const getProductName = (productId) => {
    if (!productId) return 'N/A';
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Produit inconnu';
  };

  // Get warehouse name from ID
  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return 'Non spécifié';
    const warehouse = warehouses.find(w => String(w.id) === String(warehouseId));
    return warehouse ? warehouse.name : `Entrepôt #${warehouseId}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get icon based on movement type
  const getMovementTypeIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'entrée':
        return <ArrowDownTrayIcon className="h-4 w-4 text-green-400" />;
      case 'sortie':
        return <ArrowUpTrayIcon className="h-4 w-4 text-red-400" />;
      case 'transfert':
        return <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400" />;
      case 'ajustement':
        return <ArrowPathIcon className="h-4 w-4 text-yellow-400" />;
      default:
        return <ArrowPathIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  // Get style class based on movement type
  const getMovementTypeClass = (type) => {
    switch(type?.toLowerCase()) {
      case 'entrée':
        return 'bg-green-500/20 text-green-400';
      case 'sortie':
        return 'bg-red-500/20 text-red-400';
      case 'transfert':
        return 'bg-blue-500/20 text-blue-400';
      case 'ajustement':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Filter and sort stock movements
  const filteredMovements = useMemo(() => {
    if (!stockItems || !Array.isArray(stockItems) || stockItems.length === 0) {
      console.log('No stock items found in context', stockItems);
      return [];
    }

    // Log the stockItems we have available
    console.log('Stock items available:', stockItems.length);
    
    // Process stock items to ensure all required fields are present
    let result = stockItems.map(item => {
      // Ensure quantity is available
      const quantity = item.quantity || 
                      (item.items && item.items.length > 0 
                        ? item.items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)
                        : 0);
                        
      // Ensure warehouse is available
      const warehouse = item.warehouse || item.location || 'Non spécifié';
      
      // For transfers, ensure source and destination warehouses are available
      const sourceWarehouse = item.sourceWarehouse || (item.type === 'transfert' ? warehouse : null);
      const destinationWarehouse = item.destinationWarehouse || (item.type === 'transfert' ? item.location : null);
      
      // Ensure reference is available
      const reference = item.reference || `REF-${item.id.split('-')[1] || '000'}`;
      
      return {
        ...item,
        quantity,
        warehouse,
        sourceWarehouse,
        destinationWarehouse,
        reference
      };
    });
    
    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(item => {
        const productName = getProductName(item.productId);
        const reference = item.reference || '';
        const notes = item.notes || '';
        
        return (
          productName.toLowerCase().includes(lowercasedSearch) ||
          reference.toLowerCase().includes(lowercasedSearch) ||
          notes.toLowerCase().includes(lowercasedSearch)
        );
      });
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      result = result.filter(item => item.type === typeFilter);
    }
    
    // Apply warehouse filter
    if (warehouseFilter !== 'all') {
      result = result.filter(item => String(item.warehouse) === String(warehouseFilter));
    }
    
    // Apply date filter
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      result = result.filter(item => {
        const itemDate = new Date(item.date);
        return (
          itemDate.getDate() === filterDate.getDate() &&
          itemDate.getMonth() === filterDate.getMonth() &&
          itemDate.getFullYear() === filterDate.getFullYear()
        );
      });
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Special handling for dates
        if (sortConfig.key === 'date') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return result;
  }, [stockItems, searchTerm, typeFilter, warehouseFilter, dateFilter, sortConfig, products]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // View movement details
  const handleViewMovement = (movementId) => {
    navigate(`/stock/movement/${movementId}`);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setWarehouseFilter('all');
    setDateFilter('');
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-64 flex items-center justify-center"
      >
        <div className="flex flex-col items-center">
          <ArrowPathIcon className="h-8 w-8 text-white/50 animate-spin" />
          <p className="text-white/70 mt-2">Chargement des mouvements...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
    >
      <h3 className="text-xl font-semibold text-white mb-4">Mouvements de stock</h3>
      
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
          <MagnifyingGlassIcon className="absolute top-2.5 left-3 h-5 w-5 text-white/30" />
        </div>
        
        <div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">Tous les types</option>
            <option value="entrée">Entrées</option>
            <option value="sortie">Sorties</option>
            <option value="transfert">Transferts</option>
            <option value="ajustement">Ajustements</option>
          </select>
        </div>
        
        <div>
          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">Tous les entrepôts</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
        
        <div className="relative">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
          <CalendarDaysIcon className="absolute top-2.5 left-3 h-5 w-5 text-white/30" />
        </div>
      </div>
      
      <div className="mb-4 flex justify-end">
        <button
          onClick={handleResetFilters}
          className="flex items-center px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white/70 text-sm"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Réinitialiser les filtres
        </button>
      </div>
      
      {/* Table */}
      {filteredMovements.length === 0 ? (
        <div className="bg-white/5 rounded-xl p-8 text-center">
          <ArrowPathIcon className="h-12 w-12 text-white/20 mx-auto mb-3" />
          <h4 className="text-white font-medium text-lg">Aucun mouvement trouvé</h4>
          <p className="text-white/50 mt-1">
            {stockItems && stockItems.length > 0 
              ? 'Essayez de modifier vos filtres de recherche.'
              : 'Il n\'y a pas encore de mouvements de stock enregistrés.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th 
                  className="px-4 py-3 text-left text-white/70 font-medium text-sm cursor-pointer"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center">
                    Date
                    {sortConfig.key === 'date' && (
                      <ChevronUpDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-white/70 font-medium text-sm">Type</th>
                <th className="px-4 py-3 text-left text-white/70 font-medium text-sm">Produit</th>
                <th 
                  className="px-4 py-3 text-right text-white/70 font-medium text-sm cursor-pointer"
                  onClick={() => handleSort('quantity')}
                >
                  <div className="flex items-center justify-end">
                    Quantité
                    {sortConfig.key === 'quantity' && (
                      <ChevronUpDownIcon className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-white/70 font-medium text-sm">Entrepôt</th>
                <th className="px-4 py-3 text-left text-white/70 font-medium text-sm">Référence</th>
                <th className="px-4 py-3 text-right text-white/70 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map(movement => (
                <tr 
                  key={movement.id} 
                  className="border-b border-white/5 hover:bg-white/5"
                >
                  <td className="px-4 py-3 text-white">{formatDate(movement.date)}</td>
                  <td className="px-4 py-3">
                    <span className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getMovementTypeClass(movement.type)}`}>
                      {getMovementTypeIcon(movement.type)}
                      <span>{movement.type || 'N/A'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white">{getProductName(movement.productId)}</td>
                  <td className="px-4 py-3 text-white text-right">{movement.quantity}</td>
                  <td className="px-4 py-3 text-white">
                    {movement.type === 'transfert' ? (
                      <div className="flex items-center">
                        <span>{getWarehouseName(movement.sourceWarehouse || movement.warehouse)}</span>
                        <ArrowRightIcon className="h-3 w-3 mx-1 text-blue-400" />
                        <span>{getWarehouseName(movement.destinationWarehouse || movement.warehouse)}</span>
                      </div>
                    ) : (
                      getWarehouseName(movement.warehouse)
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/70">{movement.reference || 'N/A'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleViewMovement(movement.id)}
                      className="p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white/70"
                      title="Voir les détails"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-4 flex justify-between items-center">
        <p className="text-white/50 text-sm">
          {filteredMovements.length} mouvement{filteredMovements.length !== 1 ? 's' : ''} affiché{filteredMovements.length !== 1 ? 's' : ''}
        </p>
      </div>
    </motion.div>
  );
};

export default StockMovementsTab;